import React, { useMemo } from 'react';

import { RewardPool } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useRewardPoolLeaderboardInfinityQuery,
  useSelfRewardPoolLeaderboardDataQuery,
} from '@shared/hooks/query/rewardPools';

import InfiniteQueryState from '@store:web/components/common/QueryState/InfiniteQueryState';
import { getWinningEntries } from '@store:web/components/entities/rewardPool';

import { LeaderboardTable } from './LeaderboardTable';

interface Props {
  raffleMode: 'all' | 'winner';
  rewardPool: RewardPool;
  leaderboardQuery: ReturnType<typeof useRewardPoolLeaderboardInfinityQuery>;
  leaderboardMeQuery: ReturnType<typeof useSelfRewardPoolLeaderboardDataQuery>;
}

export const LeaderboardNew = ({
  raffleMode,
  leaderboardQuery,
  leaderboardMeQuery,
  rewardPool,
}: Props) => {
  const { isAuth, user } = useAuth();
  const { data: leaderboard, isLoading: isLeaderboardLoading } = leaderboardQuery;
  const { data: leaderboardSelf } = leaderboardMeQuery;

  const allUsers = useMemo(
    () => leaderboard?.pages.flatMap((page) => page.users) || [],
    [leaderboard],
  );

  const users = leaderboardSelf ? allUsers.filter((u) => u.user_id !== user?.id) : allUsers;
  const isSelfWinner = !!getWinningEntries(leaderboardSelf?.entries)?.length;

  const totalPoints = leaderboard?.pages.length
    ? leaderboard.pages[leaderboard.pages.length - 1].points_total
    : 0;

  const showLeaderboardSelf =
    isAuth &&
    !!leaderboardSelf &&
    (raffleMode === 'all' || (raffleMode === 'winner' && isSelfWinner));

  return (
    <>
      <div className="flex flex-col gap-8">
        {showLeaderboardSelf && (
          <LeaderboardTable
            rewardPool={rewardPool}
            data={leaderboardSelf ? [leaderboardSelf] : []}
            userCellLabel="You"
            totalPoints={totalPoints}
          />
        )}
        {(isLeaderboardLoading || !!users.length) && (
          <LeaderboardTable
            rewardPool={rewardPool}
            data={users}
            userCellLabel="Member"
            isLoading={isLeaderboardLoading}
            totalPoints={totalPoints}
          />
        )}
      </div>
      <InfiniteQueryState isEmpty={!allUsers.length} query={leaderboardQuery} loading={null} />
    </>
  );
};
